import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchInput } from "../SearchInput";
import { useForm } from "react-hook-form";
import { theme } from "@src/theme";
import MultiSelect from "../MultiSelect";
import { IMood } from "@src/types/mood";
import { useFetchMoodsInfiniteQuery } from "@src/queries/tagQueries";
import { useDebounce } from "@src/hooks/useDebounce";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { setMoodsFilter } from "@src/redux/slices/moodFactorySlice";
import styled from "styled-components";
import { UIButton } from "../UIButton";

const MoodsFilterSelection = () => {
  const {
    moodsFilter: { moods: initialMoods, excludedMoods: initialExcludedMoods },
  } = useAppSelector((state) => state.moodFactory);

  const [isIncludeMode, setIsIncludeMode] = useState(true);
  const [isExcludeMode, setIsExcludeMode] = useState(false);

  const toggleMode = (mode: "include" | "exclude") => {
    setIsIncludeMode(mode === "include");
    setIsExcludeMode(mode === "exclude");
  };

  const dispatch = useAppDispatch();
  const [selectedMoods, setSelectedMoods] = useState<IMood[]>(
    initialMoods || []
  );
  const [selectedMoodsForExclude, setSelectedMoodsForExclude] = useState<
    IMood[]
  >(initialExcludedMoods || []);
  const { register, watch } = useForm({
    defaultValues: { searchText: "" },
  });
  const searchText = useDebounce<string>(watch("searchText"), 300);
  const {
    data: moodsResponse,
    isLoading: isMoodsLoading,
    isError: isMoodsError,
    error: moodsError,
    isFetching: isMoodsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchMoodsInfiniteQuery({
    searchText,
  });
  const memorizedMoodsForInclude = useMemo(() => {
    const allMoods =
      (
        (moodsResponse?.pages as AxiosResponse<ApiResponse<IMood[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || [];
    return (
      allMoods.filter(
        (mood) =>
          !selectedMoodsForExclude.some((m) => m.documentId === mood.documentId)
      ) || []
    );
  }, [moodsResponse, selectedMoodsForExclude]);

  const memorizedMoodsForExclude = useMemo(() => {
    const allMoods =
      (
        (moodsResponse?.pages as AxiosResponse<ApiResponse<IMood[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || [];
    return (
      allMoods.filter(
        (mood) => !selectedMoods.some((m) => m.documentId === mood.documentId)
      ) || []
    );
  }, [moodsResponse, selectedMoods]);

  const optionsEndRef = useRef<HTMLTableRowElement | null>(null);
  const excludeOptionsEndRef = useRef<HTMLTableRowElement | null>(null);
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );
  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    });

    if (optionsEndRef.current) observer.observe(optionsEndRef.current);
    if (excludeOptionsEndRef.current)
      observer.observe(excludeOptionsEndRef.current);
    return () => {
      if (optionsEndRef.current) observer.unobserve(optionsEndRef.current);
      if (excludeOptionsEndRef.current)
        observer.unobserve(excludeOptionsEndRef.current);
    };
  }, [handleObserver]);

  // Update redux state when selectedMoods change
  useEffect(() => {
    dispatch(
      setMoodsFilter({
        moods: selectedMoods,
        excludedMoods: selectedMoodsForExclude,
      })
    );
  }, [selectedMoods, selectedMoodsForExclude, dispatch]);

  return (
    <div>
      <div>
        <div
          style={{
            paddingBottom: theme.margins.medium,
            gap: theme.margins.small,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TopButtonsGroup>
            <UIButton
              style={{ position: "relative" }}
              onClick={() => toggleMode("include")}
              variant={isIncludeMode ? "primary" : "primaryOutline"}
            >
              Include
              <BadgeContainer isVisible={selectedMoods.length > 0}>
                <CounterBadge>+{selectedMoods.length}</CounterBadge>
              </BadgeContainer>
            </UIButton>
            <UIButton
              style={{ position: "relative" }}
              onClick={() => toggleMode("exclude")}
              variant={isExcludeMode ? "primary" : "primaryOutline"}
            >
              Exclude
              <BadgeContainer isVisible={selectedMoodsForExclude.length > 0}>
                <CounterBadge>+{selectedMoodsForExclude.length}</CounterBadge>
              </BadgeContainer>
            </UIButton>
          </TopButtonsGroup>
          <SearchInput
            placeholder="Search by mood name"
            {...register("searchText")}
          />
        </div>
        {isMoodsError ? (
          <div>Error loading moods: {moodsError.message}</div>
        ) : (
          <>
            <div
              style={{
                position: "relative",
                minHeight: 48,
              }}
            >
              <AnimatedContainer isVisible={isIncludeMode}>
                <MultiSelect<IMood>
                  key="include-moods"
                  enableCreate={false}
                  toggleCreateModal={() => {}}
                  optionEntityName="mood"
                  options={memorizedMoodsForInclude || []}
                  labelKey={"title"}
                  valueKey={"documentId"}
                  setSelected={setSelectedMoods}
                  selected={selectedMoods}
                  menuItems={[]}
                  loading={isMoodsLoading || isMoodsFetching}
                  loaderRef={optionsEndRef}
                />
              </AnimatedContainer>
              <AnimatedContainer isVisible={isExcludeMode}>
                <MultiSelect<IMood>
                  key="exclude-moods"
                  enableCreate={false}
                  toggleCreateModal={() => {}}
                  optionEntityName="mood"
                  options={memorizedMoodsForExclude || []}
                  labelKey={"title"}
                  valueKey={"documentId"}
                  setSelected={setSelectedMoodsForExclude}
                  selected={selectedMoodsForExclude}
                  menuItems={[]}
                  loading={isMoodsLoading || isMoodsFetching}
                  loaderRef={excludeOptionsEndRef}
                />
              </AnimatedContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TopButtonsGroup = styled.div`
  display: flex;
  position: relative;
  gap: ${theme.margins.small};
`;
const AnimatedContainer = styled.div<{ isVisible: boolean }>`
  transition:
    opacity 0.3s,
    transform 0.3s;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? "translateY(0)" : "translateY(50px)"};
  position: ${(props: { isVisible: boolean }) =>
    props.isVisible ? "relative" : "absolute"};
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: ${(props: { isVisible: boolean }) =>
    props.isVisible ? "auto" : "none"};
  z-index: ${(props: { isVisible: boolean }) => (props.isVisible ? 5 : -1)};
`;

const BadgeContainer = styled.span<{ isVisible?: boolean }>`
  display: ${(props) => (props.isVisible ? "inline-flex" : "none")};
  position: absolute;
  top: -5px;
  right: -5px;
  z-index: 10;
`;

const CounterBadge = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ active, theme }) =>
    active ? theme.colors.darkerBg : theme.colors.primary};
  color: ${({ active, theme }) =>
    active ? theme.colors.primary : theme.colors.darkBg};
  font-size: 0.7rem;
  padding: 2px;
  font-weight: bold;
  position: relative;
  top: -5px;
  right: -5px;
`;

export default MoodsFilterSelection;
